import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../components/customAxios';
import baseUrl from '../../base_url';
import MetricCard from '../../components/common/MetricCard';
import TrendChart from '../../components/common/TrendChart';
import {
  TrendingUp,
  ShoppingBag,
  DollarSign,
  Users,
  Package,
  ArrowRight,
  CreditCard,
  Truck,
  Boxes,
  MapPin
} from 'lucide-react';

export const AdminDashboard = () => {
  const [period, setPeriod] = useState('30d');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchAnalytics = useCallback(async (selectedPeriod) => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseUrl}api/admin/analytics?period=${selectedPeriod}`);
      if (res.data?.data) {
        setAnalytics(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics(period);
  }, [period, fetchAnalytics]);

  const kpis = analytics?.kpis || {
    grossSales: 0,
    netSales: 0,
    orders: 0,
    unitsSold: 0,
    aov: 0,
    customers: 0,
    refundsAmount: 0,
    refundsCount: 0,
    returnRate: 0,
    discounts: 0,
    shippingFees: 0
  };

  const deltas = analytics?.deltas || {
    grossSales: 0,
    netSales: 0,
    orders: 0,
    aov: 0,
    customers: 0,
    unitsSold: 0,
    refunds: 0,
    returnRate: 0
  };

  const periods = [
    { key: 'today', label: 'Today' },
    { key: '7d', label: '7D' },
    { key: '30d', label: '30D' },
    { key: '90d', label: '90D' },
    { key: 'ytd', label: 'YTD' },
    { key: 'all', label: 'All' }
  ];

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Top Header & Period Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-stone-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-stone-900 tracking-tight">Executive Business Overview</h1>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
              GBP (£)
            </span>
          </div>
          <p className="text-xs text-stone-500 mt-0.5">
            Holistic commercial performance, revenue trends, and operational bottlenecks.
          </p>
        </div>

        {/* Period Selector Tabs */}
        <div className="flex items-center gap-1 bg-stone-100 p-1 rounded-xl">
          {periods.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                period === p.key
                  ? 'bg-white text-stone-900 shadow-xs'
                  : 'text-stone-500 hover:text-stone-900'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI METRIC CARDS (6 Key Commercial Metrics) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        <MetricCard
          title="Gross Sales"
          value={kpis.grossSales.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          prefix="£"
          delta={deltas.grossSales}
          comparisonLabel="vs previous period"
          loading={loading}
          icon={TrendingUp}
        />
        <MetricCard
          title="Net Sales"
          value={kpis.netSales.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          prefix="£"
          delta={deltas.netSales}
          comparisonLabel="vs previous period"
          loading={loading}
          icon={DollarSign}
        />
        <MetricCard
          title="Total Orders"
          value={kpis.orders}
          delta={deltas.orders}
          comparisonLabel="vs previous period"
          loading={loading}
          icon={ShoppingBag}
        />
        <MetricCard
          title="Avg Order Value"
          value={kpis.aov.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          prefix="£"
          delta={deltas.aov}
          comparisonLabel="vs previous period"
          loading={loading}
          icon={TrendingUp}
        />
        <MetricCard
          title="Customers"
          value={kpis.customers}
          delta={deltas.customers}
          comparisonLabel="vs previous period"
          loading={loading}
          icon={Users}
        />
        <MetricCard
          title="Units Sold"
          value={kpis.unitsSold}
          delta={deltas.unitsSold}
          comparisonLabel="vs previous period"
          loading={loading}
          icon={Package}
        />
      </div>

      {/* PERFORMANCE & TOP PRODUCTS (Side-by-side) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TrendChart timeline={analytics?.timeline || []} defaultMetric="revenue" />

        {/* Top Products */}
        <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-stone-900 flex items-center gap-2">
                <Package className="w-4 h-4 text-stone-400" />
                Top Products by Sales
              </h3>
              <button
                onClick={() => navigate('/view-products')}
                className="text-xs text-amber-700 hover:underline font-semibold"
              >
                All Products →
              </button>
            </div>

            <div className="space-y-2.5">
              {(analytics?.topProducts || []).length === 0 ? (
                <p className="text-xs text-stone-400 py-6 text-center">No product sales in this timeframe.</p>
              ) : (
                (analytics?.topProducts || []).map((prod, idx) => (
                  <div key={prod.id || idx} className="flex items-center justify-between p-2 rounded-lg hover:bg-stone-50 transition-colors text-xs">
                    <div className="flex items-center gap-3">
                      <span className="w-5 text-center font-bold text-stone-400 font-mono">{idx + 1}</span>
                      {prod.image ? (
                        <img src={prod.image} alt="" className="w-8 h-8 rounded object-cover border border-stone-200" />
                      ) : (
                        <div className="w-8 h-8 rounded bg-stone-100 flex items-center justify-center text-stone-400">
                          <Package className="w-4 h-4" />
                        </div>
                      )}
                      <span className="font-medium text-stone-900 truncate max-w-xs">{prod.title}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-stone-900">£{prod.revenue.toFixed(2)}</div>
                      <div className="text-[11px] text-stone-400">{prod.units} units</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* OPERATIONAL BREAKDOWN SECTION */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Order Fulfillment Pipeline */}
        <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-stone-900 flex items-center gap-2">
                <Truck className="w-4 h-4 text-stone-400" />
                Fulfillment Pipeline
              </h3>
              <button
                onClick={() => navigate('/orders')}
                className="text-xs text-amber-700 hover:underline font-semibold flex items-center gap-1"
              >
                Orders <ArrowRight className="w-3 h-3" />
              </button>
            </div>

            <div className="space-y-3">
              {[
                { label: 'Placed / Needs Dispatch', count: (analytics?.byStatus?.placed || 0) + (analytics?.byStatus?.confirmed || 0), color: 'bg-amber-500' },
                { label: 'Processing & Packaging', count: analytics?.byStatus?.processing || 0, color: 'bg-indigo-500' },
                { label: 'Shipped / In Transit', count: (analytics?.byStatus?.shipped || 0) + (analytics?.byStatus?.out_for_delivery || 0), color: 'bg-sky-500' },
                { label: 'Delivered / Completed', count: (analytics?.byStatus?.delivered || 0) + (analytics?.byStatus?.completed || 0), color: 'bg-emerald-500' },
                { label: 'Cancelled / Refunded', count: (analytics?.byStatus?.cancelled || 0) + (analytics?.byStatus?.refunded || 0), color: 'bg-rose-500' },
              ].map(st => (
                <div key={st.label} className="flex items-center justify-between p-2 rounded-lg bg-stone-50 border border-stone-100 text-xs">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${st.color}`} />
                    <span className="text-stone-700 font-medium">{st.label}</span>
                  </div>
                  <span className="font-bold text-stone-900 font-mono">{st.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-stone-100 flex items-center justify-between text-xs text-stone-500">
            <span>Total Lifecycle Orders</span>
            <span className="font-bold text-stone-900">{kpis.orders}</span>
          </div>
        </div>

        {/* Inventory Health Cockpit */}
        <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-stone-900 flex items-center gap-2">
                <Boxes className="w-4 h-4 text-stone-400" />
                Catalog Stock Health
              </h3>
              <button
                onClick={() => navigate('/inventory')}
                className="text-xs text-amber-700 hover:underline font-semibold flex items-center gap-1"
              >
                Inventory <ArrowRight className="w-3 h-3" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-emerald-50/70 border border-emerald-200/80 flex items-center justify-between text-xs">
                <div>
                  <div className="font-semibold text-emerald-900">Healthy Inventory (&gt;10 units)</div>
                  <div className="text-[11px] text-emerald-700">Ready for steady sales without stockout risk</div>
                </div>
                <span className="text-lg font-bold text-emerald-950 font-mono">
                  {analytics?.inventoryHealth?.healthy || 0}
                </span>
              </div>

              <div className="p-3 rounded-lg bg-amber-50/70 border border-amber-200/80 flex items-center justify-between text-xs">
                <div>
                  <div className="font-semibold text-amber-900">Low Stock Warning (1-9 units)</div>
                  <div className="text-[11px] text-amber-700">Approaching depletion; reorder soon</div>
                </div>
                <span className="text-lg font-bold text-amber-950 font-mono">
                  {analytics?.inventoryHealth?.lowStock || 0}
                </span>
              </div>

              <div className="p-3 rounded-lg bg-rose-50/70 border border-rose-200/80 flex items-center justify-between text-xs">
                <div>
                  <div className="font-semibold text-rose-900">Out of Stock (0 units)</div>
                  <div className="text-[11px] text-rose-700">Item is active but unavailable for customers</div>
                </div>
                <span className="text-lg font-bold text-rose-950 font-mono">
                  {analytics?.inventoryHealth?.outOfStock || 0}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-stone-100 flex items-center justify-between text-xs text-stone-500">
            <span>Total Active SKUs</span>
            <span className="font-bold text-stone-900">{analytics?.inventoryHealth?.total || 0}</span>
          </div>
        </div>
      </div>

      {/* FINANCE & GEOGRAPHY SECTION */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payment Methods Breakdown */}
        <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-stone-900 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-stone-400" />
                Payment Gateway Volume
              </h3>
              <button
                onClick={() => navigate('/payments')}
                className="text-xs text-amber-700 hover:underline font-semibold flex items-center gap-1"
              >
                Payments <ArrowRight className="w-3 h-3" />
              </button>
            </div>

            <div className="space-y-3">
              {Object.entries(analytics?.byPaymentMethod || { Stripe: 0, Paypal: 0, Cod: 0 }).map(([method, count]) => (
                <div key={method} className="flex items-center justify-between p-2.5 rounded-lg bg-stone-50 border border-stone-100 text-xs">
                  <span className="font-medium text-stone-800">{method}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-stone-900 font-mono">{count} orders</span>
                    <span className="text-stone-400 text-[10px]">
                      ({kpis.orders > 0 ? Math.round((count / kpis.orders) * 100) : 0}%)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Economics Summary */}
          <div className="mt-4 pt-3 border-t border-stone-100 space-y-1 text-xs">
            <div className="flex justify-between text-stone-500">
              <span>Discounts Absorbed:</span>
              <span className="font-semibold text-stone-900">£{kpis.discounts.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-stone-500">
              <span>Shipping Fees Collected:</span>
              <span className="font-semibold text-stone-900">£{kpis.shippingFees.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Sales by UK Geography */}
        <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-stone-900 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-stone-400" />
                Sales by Delivery Location
              </h3>
              <span className="text-[11px] text-stone-400 font-medium">United Kingdom</span>
            </div>

            <div className="space-y-2.5">
              {(analytics?.geo || []).length === 0 ? (
                <p className="text-xs text-stone-400 py-6 text-center">No geographic data in this timeframe.</p>
              ) : (
                (analytics?.geo || []).map((loc, idx) => (
                  <div key={loc.city} className="flex items-center justify-between p-2 rounded-lg hover:bg-stone-50 transition-colors text-xs">
                    <div className="flex items-center gap-2.5">
                      <span className="w-5 text-center font-bold text-stone-400 font-mono">{idx + 1}</span>
                      <span className="font-medium text-stone-900">{loc.city}</span>
                    </div>
                    <div className="font-bold text-stone-900 font-mono">£{loc.revenue.toFixed(2)}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;